<script setup>
import { ref } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import { Inertia } from '@inertiajs/inertia';
import SecondaryButton from '@/Components/SecondaryButton.vue'
import PrimaryButton from '@/Components/PrimaryButton.vue'
import SelectInput from '@/Components/SelectInput.vue'
import { computed, watch } from 'vue'
import SearchSelect from "@/Components/SearchSelect.vue";
import { useTenantTextRules } from '@/helpers'

const props = defineProps({
    styles: Array,
    storages: Array,
    locations: Array,
})
const { resolveTextRule } = useTenantTextRules();

const styleNumberLabel = computed(() =>
    resolveTextRule('style_number.label', 'Style Number', 'inventory.bulk_location.style_number')
);
const garmentLabel = computed(() =>
    resolveTextRule('garment.label', 'Garment', 'inventory.bulk_location.garment')
);
const locationLabel = computed(() =>
    resolveTextRule('location.label', 'Location', 'inventory.bulk_location.location')
);
const bulkLocationTitle = computed(() =>
    resolveTextRule(
        'inventory.bulk_location.title',
        'Bulk Update Location',
        'inventory.bulk_location.title'
    )
);
const styleEntityLabel = computed(() =>
    resolveTextRule('inventory.style.entity', 'Style', 'inventory.bulk_location.style_entity')
);

const selectedStyle = ref('')
const units = ref([])
const selectedBoxes = ref([])
const selectedLocation = ref('')
const selectedStorage = ref('')
const locations = ref([])
const storages = ref([])
const modalOpen = ref(false)

const loadUnits = async () => {
    try {
        const response = await fetch(`/inventory/boxes-by-style/${selectedStyle.value}`);
        const data = await response.json();
        units.value = data.units || [];
        console.log('Units loaded:', units.value);
    } catch (error) {
        console.error('Error when loading boxes', error);
    }
}

const updateLocation = () => {
    const payload = {
        boxes: selectedBoxes.value,
        locationId: selectedLocation.value,
        storageId: selectedStorage.value,
    }
    if (selectedLocationType.value === 'warehouse' || selectedLocationType.value === 'container') {
        payload.row = row.value || null
        payload.rack = rack.value || null
        payload.shelf = shelf.value || null
    }
    if (selectedLocationType.value === 'conveyor') {
        payload.conveyor_slot = conveyorSlot.value || null
    }

    Inertia.put('/inventory/bulk-update-location', payload, {
        preserveScroll: true,
        onSuccess: () => {
            modalOpen.value = false
            selectedBoxes.value = []
            selectedStyle.value = ''
            units.value = []
            selectedLocation.value = ''
            selectedStorage.value = ''
            row.value = ''
            rack.value = ''
            shelf.value = ''
            conveyorSlot.value = ''
        },
        onError: (error) => {
            console.error('Error updating location:', error);
        }
    });
}

function goBack() {
    window.history.back();
}

const row = ref('')
const rack = ref('')
const shelf = ref('')
const conveyorSlot = ref('')

const selectedLocationType = computed(() => {
    return props.storages.find(loc => loc.locationId === selectedLocation.value)?.type || ''
})

watch(selectedLocation, () => {
    selectedStorage.value = ''
    row.value = ''
    rack.value = ''
    shelf.value = ''
    conveyorSlot.value = ''
})

const styleOptions = computed(() =>
    (props.styles || [])
        .filter(s => s?.styleNumber && s?.styleId != null)
        .map(s => ({ label: s.styleNumber, value: Number(s.styleId) }))
);

const styleModel = computed({
    get: () => (selectedStyle.value ? Number(selectedStyle.value) : null),
    set: (v) => { selectedStyle.value = v == null ? '' : Number(v); },
});

</script>


<template>
    <AppLayout :title="bulkLocationTitle">
        <div class="p-6">
            <h2 class="text-xl font-bold mb-4">{{ bulkLocationTitle }}</h2>

            <div class="mb-4">
                <SecondaryButton @click="goBack">
                    ← Back
                </SecondaryButton>
            </div>

            <h3>Select {{ styleEntityLabel }}</h3>
            <SearchSelect v-model="styleModel" :options="styleOptions" :placeholder="`-- Select a ${styleEntityLabel} --`"
                @change="loadUnits" />

            <div class="m-2 rounded overflow-auto">

                <table class="min-w-full w-full text-left text-sm font-light m-4 px-4" v-if="units.length">
                    <thead class="border-b font-medium dark:border-neutral-500">
                        <tr>
                            <th class="px-3 py-2 text-left"><input type="checkbox"
                                    @change="e => selectedBoxes = e.target.checked ? units.map(u => u.id) : []"></th>
                            <th class="px-3 py-2 text-left">{{ locationLabel }}</th>
                            <th class="px-3 py-2 text-left">Storage Identifier</th>
                            <th class="px-3 py-2 text-left">Box</th>
                            <th class="px-3 py-2 text-left">{{ styleNumberLabel }}</th>
                            <th class="px-3 py-2 text-left">Gender</th>
                            <th class="px-3 py-2 text-left">{{ garmentLabel }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="unit in units" :key="unit.id" class="border-b border-gray-200 hover:bg-gray-100">
                            <td class="px-3 py-2"><input type="checkbox" :value="unit.id" v-model="selectedBoxes"></td>
                            <td class="px-3 py-2">{{ unit.location }}</td>
                            <td class="px-3 py-2">
                                {{
                                    (() => {
                                        const locationMatch = props.locations.find(
                                            loc => loc.name === unit.location
                                        );
                                        const locationIdentifier = locationMatch?.identifier || '';
                                        const storageIdentifier = unit.container || unit.warehouse || unit.conveyor || '';
                                        return [locationIdentifier, storageIdentifier].filter(Boolean).join('_');
                                    })()
                                }}
                            </td>
                            <td class="px-3 py-2">{{ unit.box }}</td>
                            <td class="px-3 py-2">{{ unit.styleNumber }}</td>
                            <td class="px-3 py-2">{{ unit.gender }}</td>
                            <td class="px-3 py-2">{{ unit.garmentName }}</td>
                        </tr>
                    </tbody>
                </table>

            </div>
            <div v-if="selectedStyle">
                <h4 for="merge-box">
                    Select new {{ locationLabel.toLowerCase() }}
                </h4>
                <SelectInput id="merge-box" v-model="selectedLocation" class="mt-1">
                    <option value="">Select a {{ locationLabel }}</option>
                    <option v-for="loc in props.locations" :key="loc.locationId" :value="loc.locationId">
                        {{ loc.name }}
                    </option>
                </SelectInput>
            </div>

            <div v-if="selectedLocation">
                <h4 for="storage" class="mt-4">Select new storage</h4>

                <SelectInput id="storage" v-model="selectedStorage" class="mt-1">
                    <option value="">Select a Storage</option>
                    <option v-for="s in props.storages.filter(storage => storage.locationId === selectedLocation)"
                        :key="s.id" :value="s.id">
                        {{ s.name }}
                    </option>
                </SelectInput>

            </div>

            <div class="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-4"
                v-if="selectedLocationType === 'warehouse' || selectedLocationType === 'container'">
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Row</label>
                    <input type="text" v-model="row"
                        class="border border-gray-300 rounded-md shadow-sm px-3 py-2 w-full" />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Rack</label>
                    <input type="text" v-model="rack"
                        class="border border-gray-300 rounded-md shadow-sm px-3 py-2 w-full" />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Shelf</label>
                    <input type="text" v-model="shelf"
                        class="border border-gray-300 rounded-md shadow-sm px-3 py-2 w-full" />
                </div>
                <div class="flex items-end">
                    <div class="text-xs text-gray-500">
                        These fields apply to all selected boxes.
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-4" v-if="selectedLocationType === 'conveyor'">
                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        Conveyor Slot
                    </label>
                    <input type="number" min="1" v-model.number="conveyorSlot"
                        class="border border-gray-300 rounded-md shadow-sm px-3 py-2 w-full" />
                </div>
                <div class="flex items-end">
                    <div class="text-xs text-gray-500">
                        This slot will be applied to all selected boxes.
                    </div>
                </div>
            </div>

            <PrimaryButton class="text-white px-4 py-2 mt-6" v-if="units.length"
                :disabled="!selectedBoxes.length || !selectedLocation || !selectedStorage" @click="updateLocation">
                Update Location
            </PrimaryButton>


        </div>
    </AppLayout>
</template>
